import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence, useMotionValue, useTransform, animate } from "framer-motion";
import { ArrowRight, Check, Focus, GraduationCap, Sparkles, Settings2, Baby, User } from "lucide-react";
import { track } from "../lib/analytics";
import { API } from "../api";
import FocusJourneyScene from "./FocusJourneyScene";

/* ---------- Live demo: chapter art "builds" as you read ---------- */
function ArtDemo() {
  const p = useMotionValue(0);
  useEffect(() => {
    const c = animate(p, [0, 1, 1, 0], { duration: 5, times: [0, 0.45, 0.82, 1], ease: "easeInOut", repeat: Infinity });
    return () => c.stop();
  }, [p]);
  const opacity = useTransform(p, [0, 1], [0.22, 1]);
  const blur = useTransform(p, [0, 1], [14, 0]);
  const filter = useTransform(blur, (b) => `blur(${b}px) saturate(1.1) brightness(1.08)`);
  const scale = useTransform(p, [0, 1], [1.14, 1]);
  const textOpacity = useTransform(p, [0.25, 0.7], [0.15, 1]);
  return (
    <div className="relative mb-7 h-60 w-full max-w-sm overflow-hidden rounded-3xl border border-[#F2C14E]/25 shadow-2xl">
      <motion.div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${API}/bible/chapter-image/GEN/1?v=2)`, opacity, filter, scale }} />
      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-black/30" />
      <motion.div className="absolute inset-x-0 bottom-0 p-5 text-left" style={{ opacity: textOpacity }}>
        <p className="font-serif-display text-lg leading-snug text-white drop-shadow-lg">"And God said, Let there be light: and there was light."</p>
        <p className="font-ui mt-1.5 text-xs text-white/75">Genesis 1 · the scene paints itself as you read</p>
      </motion.div>
    </div>
  );
}

/* ---------- Live demo: Focus Mode spotlights one verse ---------- */
const FOCUS_LINES = [
  "In the beginning was the Word,",
  "and the Word was with God,",
  "and the Word was God.",
  "In him was life,",
  "and that life was the light of all.",
];
function FocusDemo() {
  const [active, setActive] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setActive((a) => (a + 1) % FOCUS_LINES.length), 1250);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="mb-7 w-full max-w-sm space-y-3 rounded-3xl border border-[#F2C14E]/20 bg-white/[0.04] p-7 text-left backdrop-blur-sm">
      {FOCUS_LINES.map((l, idx) => {
        const on = idx === active;
        return (
          <p key={idx} className="font-serif-display text-lg leading-snug text-[#FDEBD0] transition-all duration-500"
            style={{ opacity: on ? 1 : 0.26, filter: on ? "none" : "blur(1.6px)", transform: on ? "scale(1.04)" : "scale(1)", transformOrigin: "left" }}>
            {l}
          </p>
        );
      })}
    </div>
  );
}

/* ---------- Live demo: Explain — toolbar button + what you see ---------- */
const EXPLAIN_LEVELS = [
  { label: "Child", sub: "Age 7", icon: Baby, text: "Before anything existed, Jesus was already there with God. He made everything — and He's like a bright light that helps us see God." },
  { label: "Teen", sub: "Age 15", icon: Sparkles, text: "John calls Jesus “the Word” — God's self-expression. He existed with God and as God, and through Him everything was made." },
  { label: "Adult", sub: "General", icon: User, text: "John identifies Jesus as the eternal Word who was with God and was God. All things were made through Him; in Him is life and the light of humanity." },
  { label: "Scholar", sub: "In depth", icon: GraduationCap, text: "The prologue's “Logos” echoes Genesis 1, affirming Christ's pre-existence, deity, and agency in creation — the incarnation as light entering darkness." },
];
function ExplainDemo() {
  const [active, setActive] = useState(2);
  useEffect(() => {
    const id = setInterval(() => setActive((a) => (a + 1) % EXPLAIN_LEVELS.length), 2200);
    return () => clearInterval(id);
  }, []);
  const lvl = EXPLAIN_LEVELS[active];
  return (
    <div className="mb-7 w-full max-w-sm space-y-3">
      {/* mock reading toolbar with the Explain button highlighted */}
      <div className="flex items-center justify-center gap-3 rounded-2xl border border-white/10 bg-white/[0.05] px-4 py-3">
        <Focus className="h-4 w-4 text-[#FDEBD0]/45" />
        <span className="flex animate-pulse items-center gap-1.5 rounded-full bg-[#F2C14E] px-3 py-1.5 text-xs font-bold text-[#231708] shadow-[0_0_18px_rgba(242,193,78,0.65)]">
          <GraduationCap className="h-3.5 w-3.5" /> Explain
        </span>
        <Sparkles className="h-4 w-4 text-[#FDEBD0]/45" />
        <Settings2 className="h-4 w-4 text-[#FDEBD0]/45" />
      </div>
      <p className="text-center font-ui text-[11px] text-[#FDEBD0]/55">Tap “Explain” in any chapter…</p>

      {/* mock explain panel — what you see */}
      <div className="rounded-3xl border border-[#F2C14E]/20 bg-white/[0.04] p-5 backdrop-blur-sm">
        <p className="font-serif-display mb-3 text-base text-[#FDEBD0]">Explain · John 1</p>
        <div className="mb-4 grid grid-cols-4 gap-1.5">
          {EXPLAIN_LEVELS.map((l, idx) => {
            const on = idx === active;
            return (
              <div key={l.label} className={`flex flex-col items-center gap-0.5 rounded-xl border px-1 py-2 text-center transition-all ${on ? "border-[#F2C14E] bg-[#F2C14E]/15 text-[#F2C14E]" : "border-white/10 text-[#FDEBD0]/45"}`}>
                <l.icon className="h-3.5 w-3.5" />
                <span className="text-[10px] font-semibold">{l.label}</span>
              </div>
            );
          })}
        </div>
        <p key={active} className="reading-text animate-in fade-in text-sm leading-relaxed text-[#FDEBD0]/85">{lvl.text}</p>
      </div>
    </div>
  );
}

/* ---------- Live demo: Reading Journey — book tiles fill up ---------- */
function JourneyDemo() {
  const [p, setP] = useState(28);
  useEffect(() => {
    const id = setInterval(() => setP((v) => (v >= 82 ? 28 : v + 6)), 240);
    return () => clearInterval(id);
  }, []);
  const Tile = ({ name, pct, full }) => (
    <div
      className={`rounded-xl p-3 ${
        full ? "bg-[#E0B85C] text-[#3A2E12] shadow-[0_0_14px_rgba(224,184,92,0.45)]" : "border border-white/10 bg-white/[0.04]"
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className={`font-ui truncate text-sm font-semibold ${full ? "" : "text-[#FDEBD0]"}`}>{name}</span>
        {full ? <Check className="h-4 w-4 shrink-0" /> : <span className="font-ui shrink-0 text-xs font-semibold text-[#E0B85C]">{pct}%</span>}
      </div>
      {!full && (
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-white/10">
          <div className="h-full rounded-full bg-[#E0B85C] transition-all duration-200" style={{ width: `${pct}%` }} />
        </div>
      )}
    </div>
  );
  return (
    <div className="mb-7 w-full max-w-sm rounded-3xl border border-[#F2C14E]/20 bg-white/[0.04] p-6 text-left backdrop-blur-sm">
      <div className="mb-3 flex items-center justify-between">
        <span className="font-ui text-[0.6rem] font-semibold uppercase tracking-[0.15em] text-[#F2C14E]">Reading Journey</span>
      </div>
      {/* mini stats dashboard */}
      <div className="mb-4 grid grid-cols-3 gap-2">
        {[
          { v: "42%", l: "Bible" },
          { v: "612", l: "Chapters" },
          { v: "12", l: "Streak" },
        ].map((s) => (
          <div key={s.l} className="rounded-xl border border-white/10 bg-white/[0.04] px-2 py-2.5 text-center">
            <p className="font-serif-display text-xl text-[#E0B85C]">{s.v}</p>
            <p className="font-ui text-[0.55rem] uppercase tracking-wider text-[#FDEBD0]/60">{s.l}</p>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2.5">
        <Tile name="Matthew" full />
        <Tile name="John" pct={p} />
        <Tile name="Romans" pct={54} />
        <Tile name="Mark" full />
      </div>
    </div>
  );
}

/* ---------- Mock view: Reading Plans ---------- */
function PlanDemo() {
  return (
    <div className="relative mb-7 h-60 w-full max-w-sm overflow-hidden rounded-3xl border border-white/10 shadow-2xl">
      <img src="/cards/plans.jpg" alt="" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-black/25" />
      <div className="absolute inset-x-0 bottom-0 p-5 text-left">
        <span className="font-ui inline-block rounded-full bg-[#F2C14E]/20 px-3 py-1 text-[0.6rem] font-semibold uppercase tracking-[0.15em] text-[#F2C14E]">Reading Plan</span>
        <p className="font-serif-display mt-2.5 text-2xl text-white drop-shadow">Bible in a Year</p>
        <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-white/20">
          <motion.div className="h-full rounded-full bg-[#F2C14E]" initial={{ width: "0%" }} animate={{ width: "34%" }} transition={{ duration: 1.4, ease: "easeOut" }} />
        </div>
        <p className="font-ui mt-2 text-xs text-white/85">Day 122 of 365 · Today: Psalm 23</p>
      </div>
    </div>
  );
}

/* ---------- Mock view: Daily Devotionals ---------- */
function DevoDemo() {
  return (
    <div className="relative mb-7 h-60 w-full max-w-sm overflow-hidden rounded-3xl border border-white/10 shadow-2xl">
      <img src="/cards/devotionals.jpg" alt="" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/30" />
      <div className="absolute inset-x-0 bottom-0 p-5 text-left">
        <span className="font-ui inline-block rounded-full bg-[#5C8A6E]/30 px-3 py-1 text-[0.6rem] font-semibold uppercase tracking-[0.15em] text-emerald-200">Hope · Day 1</span>
        <p className="font-serif-display mt-2.5 text-2xl text-white drop-shadow">Morning &amp; Evening</p>
        <p className="font-ui mt-2 line-clamp-2 text-xs leading-relaxed text-white/85">
          "Why are you cast down, O my soul?… Hope in God." A fresh reading, a modern story, and a short prayer — every single day.
        </p>
      </div>
    </div>
  );
}

/* ---------- Mock view: Word Study — original languages ---------- */
const WORD_ORIGINALS = [
  { lang: "Greek", word: "ἀγάπη", translit: "agapē", meaning: "selfless, sacrificial love — the love God has for us." },
  { lang: "Hebrew", word: "אַהֲבָה", translit: "ahavah", meaning: "deep affection and covenant devotion between people and God." },
  { lang: "Greek", word: "φιλέω", translit: "phileō", meaning: "warm, brotherly love — the affection of close friends." },
];
function WordStudyDemo() {
  const [active, setActive] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setActive((a) => (a + 1) % WORD_ORIGINALS.length), 2000);
    return () => clearInterval(id);
  }, []);
  const o = WORD_ORIGINALS[active];
  const chips = ["love", "grace", "faith", "peace"];
  return (
    <div className="mb-7 w-full max-w-sm space-y-3">
      <div className="flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.05] px-4 py-3">
        <span className="text-[#FDEBD0]/50">🔍</span>
        <span className="font-ui text-sm text-[#FDEBD0]/85">love</span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {chips.map((c, idx) => (
          <span key={c} className={`font-ui rounded-full border px-3 py-1 text-xs ${idx === 0 ? "border-[#F2C14E] bg-[#F2C14E]/15 text-[#F2C14E]" : "border-white/10 text-[#FDEBD0]/50"}`}>{c}</span>
        ))}
      </div>
      <div key={active} className="animate-in fade-in rounded-3xl border border-[#F2C14E]/20 bg-white/[0.04] p-5 text-left backdrop-blur-sm">
        <span className="font-ui inline-block rounded-full bg-[#3E5A6B] px-3 py-1 text-[0.6rem] font-semibold uppercase tracking-[0.15em] text-white">{o.lang}</span>
        <p className="font-serif-display mt-2.5 text-3xl text-[#FDEBD0]" dir="auto">{o.word}</p>
        <p className="font-ui text-base text-[#F2C14E]">{o.translit}</p>
        <p className="reading-text mt-2 text-sm leading-relaxed text-[#FDEBD0]/85">{o.meaning}</p>
      </div>
    </div>
  );
}

const SLIDES = [
  { kind: "welcome", title: "Welcome to Clarity Bible", body: "Scripture that holds your attention — a calm, visual Bible built for wandering minds." },
  { kind: "art", title: "The page comes alive", body: "As you read, each chapter quietly paints itself into a scene — a moving work of art that pulls you deeper into the story." },
  { kind: "focus", title: "One verse at a time", body: "Focus Mode spotlights the verse you're on and softens the rest, so a wandering mind has nowhere to drift." },
  { kind: "explain", title: "Stuck? Tap “Explain”", body: "In any chapter, tap Explain in the toolbar for a clear, plain-language summary — at four levels, from a 7-year-old to a scholar." },
  { kind: "wordstudy", title: "New: Word Study", body: "Ever wonder what a word truly meant? Search any word — like love or grace — and see it in the original Hebrew, Greek & Aramaic, with meaning and key verses.", isNew: true },
  { kind: "plan", title: "Follow a guided plan", body: "Bible-in-a-Year and topical journeys gently lead you to the next chapter — no deciding, just reading." },
  { kind: "devo", title: "A devotional every morning", body: "Genuine Spurgeon readings by theme, each paired with a modern real-life story, art, and a short prayer." },
  { kind: "journey", title: "See your journey at a glance", body: "Every book shows how much you've read, and finished books shine gold. Tap any book to see what's left — and log reading you did elsewhere, like a paper Bible." },
  { kind: "tree", title: "Watch your reading bloom", body: "Every chapter grows your Focus Journey tree — from a seed at night to full morning bloom. A living picture of your time in God's Word." },
];

export default function Onboarding({ onDone }) {
  const [i, setI] = useState(0);
  const last = i === SLIDES.length - 1;
  const slide = SLIDES[i];

  const finish = () => {
    localStorage.setItem("fw_onboarded", "1");
    track("onboarding_complete", { slides: SLIDES.length });
    onDone();
  };
  const next = () => (last ? finish() : setI(i + 1));
  const prev = () => setI((v) => Math.max(0, v - 1));
  const skip = () => {
    localStorage.setItem("fw_onboarded", "1");
    track("onboarding_skipped", { at: i + 1 });
    onDone();
  };
  // Guard so a swipe isn't also counted as a tap-to-advance.
  const didDrag = useRef(false);
  const onDragEnd = (_, info) => {
    if (Math.abs(info.offset.x) > 5) {
      didDrag.current = true;
      setTimeout(() => { didDrag.current = false; }, 80);
    }
    if (info.offset.x < -60) next();
    else if (info.offset.x > 60) prev();
  };
  const handleTap = () => { if (!didDrag.current) next(); };

  return (
    <div
      data-testid="onboarding-overlay"
      className="fixed inset-0 z-[120] flex flex-col overflow-y-auto px-6 py-7 text-[#FDEBD0]"
      style={{ background: "radial-gradient(125% 80% at 50% 0%, #1d2a52 0%, #131a33 42%, #0b1020 100%)" }}
    >
      {/* warm dawn glow on the horizon + soft gold halo up top */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2" style={{ background: "radial-gradient(70% 60% at 50% 100%, rgba(242,193,78,0.22), transparent 70%)" }} />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-1/3" style={{ background: "radial-gradient(50% 70% at 50% 0%, rgba(124,154,200,0.18), transparent 70%)" }} />

      {/* header: logo */}
      <div className="relative z-10 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <img src="/logo_mark.png" alt="Clarity Bible" className="h-9 w-9 object-contain drop-shadow" />
          <span className="font-serif-display text-lg font-semibold tracking-tight text-[#FDEBD0]">Clarity Bible</span>
        </div>
        <div className="flex items-center gap-3">
        <span className="font-ui text-xs font-medium text-[#FDEBD0]/45">{i + 1} / {SLIDES.length}</span>
        {!last && (
          <button
            onClick={skip}
            data-testid="onboarding-skip"
            className="font-ui rounded-full px-3 py-1.5 text-xs font-semibold text-[#FDEBD0]/60 transition-colors hover:bg-white/10 hover:text-[#FDEBD0]"
          >
            Skip
          </button>
        )}
        </div>
      </div>

      <div className="relative z-10 flex flex-1 flex-col items-center justify-center py-6 text-center">
        <AnimatePresence mode="wait">
          <motion.div
            key={i}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.35}
            onDragEnd={onDragEnd}
            onClick={handleTap}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -24 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="flex w-full max-w-md cursor-grab flex-col items-center active:cursor-grabbing"
          >
            {slide.kind === "art" && <ArtDemo />}
            {slide.kind === "focus" && <FocusDemo />}
            {slide.kind === "explain" && <ExplainDemo />}
            {slide.kind === "wordstudy" && <WordStudyDemo />}
            {slide.kind === "plan" && <PlanDemo />}
            {slide.kind === "devo" && <DevoDemo />}
            {slide.kind === "journey" && <JourneyDemo />}
            {slide.kind === "tree" && (
              <div className="mb-7 w-full max-w-xs">
                <FocusJourneyScene progress={0.85} heightClass="h-56" />
              </div>
            )}
            {slide.kind === "welcome" && (
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="mb-8 flex h-32 w-32 items-center justify-center rounded-full border border-[#F2C14E]/30 bg-white/[0.06] shadow-[0_8px_40px_rgba(242,193,78,0.35)] backdrop-blur-sm"
              >
                <img src="/logo_mark.png" alt="Clarity Bible" className="h-20 w-20 object-contain" />
              </motion.div>
            )}
            {slide.isNew && (
              <span className="font-ui mb-3 inline-block rounded-full bg-[#F2C14E] px-3 py-1 text-[0.6rem] font-bold uppercase tracking-[0.2em] text-[#231708]">New feature</span>
            )}
            <h2 className="font-serif-display bg-gradient-to-b from-white to-[#F2D9A0] bg-clip-text text-3xl leading-tight text-transparent sm:text-4xl">
              {slide.title}
            </h2>
            <p className="font-ui mt-4 text-base leading-relaxed text-[#D8DCEA]">{slide.body}</p>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="relative z-10 mx-auto w-full max-w-md">
        <div className="mb-6 flex justify-center gap-2" data-testid="onboarding-dots">
          {SLIDES.map((_, idx) => (
            <span key={idx} className={`h-2 rounded-full transition-all duration-300 ${idx === i ? "w-6 bg-[#F2C14E]" : "w-2 bg-white/25"}`} />
          ))}
        </div>
        <p className="font-ui mb-3 text-center text-[11px] text-[#FDEBD0]/40">Tap or swipe to continue</p>
        <button
          onClick={next}
          data-testid={last ? "onboarding-start" : "onboarding-next"}
          className="font-ui flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#F2C14E] to-[#E0A24C] py-4 text-base font-bold text-[#231708] shadow-lg shadow-[#F2C14E]/20 transition-transform hover:scale-[1.02]"
        >
          {last ? (<>Get started <Check className="h-5 w-5" /></>) : (<>Next <ArrowRight className="h-5 w-5" /></>)}
        </button>
      </div>
    </div>
  );
}
